import { Fragment, useMemo, useState } from 'react';

import {
  Box,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip
} from '@mui/material';

import { Description } from '@mui/icons-material';
import { ChevronDown, ChevronRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

import { config } from 'src/config';
import { formatDate } from 'src/utils/date';
import { formatMoney } from 'src/utils/formatters';

const applyFilters = (jobs, filters) => {
  return (jobs || []).filter((job) => {
    let matches = true;

    if (filters.status && job.status !== filters.status) {
      matches = false;
    }

    return matches;
  });
};

const applyPagination = (items, page: number, limit: number) => {
  return items.slice(page * limit, page * limit + limit);
};

const getJobNumber = (job) =>
  job?.shift?.Event?.title || job?.Event?.title || 'N/A';

const getEventId = (job) => job?.shift?.Event?.id || job?.Event?.id;

const groupJobsByJobNumber = (jobs) => {
  return (jobs || []).reduce((acc, job) => {
    const jobNumber = getJobNumber(job);
    if (!acc[jobNumber]) {
      acc[jobNumber] = [];
    }
    acc[jobNumber].push(job);
    return acc;
  }, {});
};

const StarRating = ({
  rating,
  size = 16
}: {
  rating: number;
  size?: number;
}) => {
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => {
        const isFull = i < Math.floor(rating);
        const isHalf = i < rating && !isFull;

        return (
          <Star
            key={i}
            size={size}
            fill={isFull || isHalf ? 'currentColor' : 'none'}
            strokeWidth={isFull || isHalf ? 0 : 1.5}
            className={
              isFull
                ? 'text-yellow-600'
                : isHalf
                  ? 'text-yellow-300'
                  : 'text-gray-300'
            }
          />
        );
      })}

      <span className="text-sm text-muted-foreground ml-1">
        {rating.toFixed(1)}
      </span>
    </div>
  );
};

const JobHistoryTable = ({ Job }) => {
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(5);
  const [filters] = useState({
    status: null
  });
  const [expandedJobNumbers, setExpandedJobNumbers] = useState<Set<string>>(
    new Set()
  );

  const filteredJobs = useMemo(
    () => applyFilters(Job, filters),
    [Job, filters]
  );
  const groupedJobs = useMemo(
    () => groupJobsByJobNumber(filteredJobs),
    [filteredJobs]
  );
  const jobNumbers = useMemo(
    () => Object.keys(groupedJobs).sort((a, b) => a.localeCompare(b)),
    [groupedJobs]
  );
  const paginatedJobNumbers = applyPagination(jobNumbers, page, limit);

  const handlePageChange = (event, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExpandClick = (jobNumber: string) => {
    setExpandedJobNumbers((current) => {
      const next = new Set(current);
      if (next.has(jobNumber)) {
        next.delete(jobNumber);
      } else {
        next.add(jobNumber);
      }
      return next;
    });
  };

  return (
    <Fragment>
      <Stack sx={{ width: '100%', overflow: 'auto' }}>
        <TableContainer sx={{ minWidth: '1500px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px" />
                <TableCell>Job Number</TableCell>
                <TableCell>Job Count</TableCell>
                <TableCell>Total Invoice</TableCell>
                <TableCell>Total Paid</TableCell>
                <TableCell>Payment Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedJobNumbers.map((jobNumber) => {
                const jobsForJob = groupedJobs[jobNumber];
                const isExpanded = expandedJobNumbers.has(jobNumber);
                const totalInvoice = jobsForJob.reduce(
                  (sum, job) =>
                    sum + (Number(job?.contractorInvoiceTotal) || 0),
                  0
                );
                const totalPaid = jobsForJob.reduce(
                  (sum, job) => sum + (Number(job?.totalPaidContractor) || 0),
                  0
                );
                const statuses = Array.from(
                  new Set(
                    jobsForJob
                      .map((job) => job?.shiftPaymentStatus)
                      .filter(Boolean)
                  )
                );

                return (
                  <Fragment key={jobNumber}>
                    <TableRow
                      sx={{
                        backgroundColor: '#000000',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#1a1a1a'
                        }
                      }}
                      onClick={() => handleExpandClick(jobNumber)}
                    >
                      <TableCell sx={{ color: '#ffffff' }}>
                        <IconButton size="small">
                          {isExpanded ? (
                            <ChevronDown size={18} color="#ffffff" />
                          ) : (
                            <ChevronRight size={18} color="#ffffff" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#E8BF21' }}>
                        {jobNumber}
                      </TableCell>
                      <TableCell sx={{ color: '#ffffff' }}>
                        {jobsForJob.length}
                      </TableCell>
                      <TableCell sx={{ color: '#ffffff' }}>
                        {formatMoney(totalInvoice)}
                      </TableCell>
                      <TableCell sx={{ color: '#ffffff' }}>
                        {formatMoney(totalPaid)}
                      </TableCell>
                      <TableCell sx={{ color: '#ffffff' }}>
                        {statuses.length === 1
                          ? statuses[0]
                          : statuses.length > 1
                            ? 'Mixed'
                            : 'N/A'}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 2 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                  <TableCell>Job Number</TableCell>
                                  <TableCell>Position</TableCell>
                                  <TableCell>Start Date</TableCell>
                                  <TableCell>Clock Status</TableCell>
                                  <TableCell>Invoice Total</TableCell>
                                  <TableCell>
                                    Last Minute Cancellations
                                  </TableCell>
                                  <TableCell>Invoice Status</TableCell>
                                  <TableCell>Payout Method</TableCell>
                                  <TableCell>Date Paid</TableCell>
                                  <TableCell>Payment Status</TableCell>
                                  <TableCell>Total Payout</TableCell>
                                  <TableCell>Instant Pay</TableCell>
                                  <TableCell>Client Review</TableCell>
                                  <TableCell>Lead Review</TableCell>
                                  <TableCell>Lead Name</TableCell>
                                  <TableCell>Invoice</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {jobsForJob.map((job) => (
                                  <TableRow hover key={job.id}>
                                    <TableCell>
                                      <Link
                                        to={`/dashboards/jobTracker/positions/${getEventId(
                                          job
                                        )}`}
                                        state={{ title: getJobNumber(job) }}
                                      >
                                        <span
                                          style={{
                                            color: 'blue',
                                            textDecoration: 'underline'
                                          }}
                                        >
                                          {getJobNumber(job)}
                                        </span>
                                      </Link>
                                    </TableCell>
                                    <TableCell>{job?.position}</TableCell>
                                    <TableCell>
                                      {formatDate(job?.startDate)}
                                    </TableCell>
                                    <TableCell>
                                      {job && (
                                        <>
                                          {job?.clockIn ? (
                                            (() => {
                                              const clockInTime = job?.clockIn
                                                ?.split('T')[1]
                                                ?.slice(0, 5);
                                              const timeDifference =
                                                getTimeDifference(
                                                  job?.startTime,
                                                  clockInTime
                                                );
                                              const formatted =
                                                formatLateTime(timeDifference);

                                              if (timeDifference <= 5) {
                                                return (
                                                  <span className="text-green-600 font-medium">
                                                    On Time
                                                  </span>
                                                );
                                              }
                                              if (timeDifference > 15) {
                                                return (
                                                  <span className="text-red-600 font-medium">
                                                    Late ({formatted})
                                                  </span>
                                                );
                                              }
                                              return (
                                                <span className="text-orange-500 font-medium">
                                                  Late ({formatted})
                                                </span>
                                              );
                                            })()
                                          ) : job?.shift?.status ===
                                            'completed' ? (
                                            <span className="text-gray-500 font-medium">
                                              NCNS
                                            </span>
                                          ) : (
                                            <span className="text-yellow-500 font-medium">
                                              Pending
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </TableCell>

                                    <TableCell>
                                      {formatMoney(
                                        job?.contractorInvoiceTotal || 0
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {job?.lastMinuteCancellation ? (
                                        <input
                                          type="checkbox"
                                          checked={job?.lastMinuteCancellation}
                                          readOnly
                                        />
                                      ) : (
                                        <input
                                          type="checkbox"
                                          disabled
                                          checked={false}
                                          readOnly
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {job?.contractorInvoiceStatus}
                                    </TableCell>
                                    <TableCell>
                                      {job?.payoutMethod ||
                                        job?.prefferedPayoutMethod ||
                                        'N/A'}
                                    </TableCell>

                                    <TableCell>
                                      {job?.datePaid
                                        ? new Date(
                                            job.datePaid
                                          ).toLocaleDateString()
                                        : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {job?.shiftPaymentStatus}
                                    </TableCell>

                                    <TableCell>
                                      {formatMoney(
                                        job?.totalPaidContractor || 0
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {job?.instantPay ? (
                                        <input
                                          type="checkbox"
                                          checked={job?.instantPay}
                                          readOnly
                                        />
                                      ) : (
                                        <input
                                          type="checkbox"
                                          disabled
                                          checked={false}
                                          readOnly
                                        />
                                      )}
                                    </TableCell>

                                    <TableCell>-</TableCell>
                                    <TableCell>
                                      {job?.professionalReview > 0 ? (
                                        <div className="flex items-center gap-2">
                                          <StarRating
                                            rating={job?.professionalReview}
                                            size={14}
                                          />
                                        </div>
                                      ) : (
                                        <span className="text-muted-foreground">
                                          -
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {job?.shift?.LeadUser?.firstName}{' '}
                                      {job?.shift?.LeadUser?.lastName}
                                    </TableCell>
                                    <TableCell>
                                      <IconButton
                                        disabled={!job?.contractorInvoice}
                                        size="small"
                                        color="primary"
                                        component="a"
                                        href={
                                          config.FILe_URL +
                                          job?.contractorInvoice
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <Tooltip title="View Invoice" arrow>
                                          <Description fontSize="small" />
                                        </Tooltip>
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
      <Box
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <TablePagination
          component="div"
          count={jobNumbers.length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[5, 10, 25, 30]}
        />
        <Stack>
          <div className="text-base font-semibold">
            Total Paid:{' '}
            <span className="text-green-600 font-bold text-lg">
              {formatMoney(
                (Job || []).reduce(
                  (acc, job) =>
                    job?.shiftPaymentStatus === 'Paid'
                      ? acc + (Number(job?.totalPaidContractor) || 0)
                      : acc,
                  0
                )
              )}
            </span>
          </div>
        </Stack>
      </Box>
    </Fragment>
  );
};

export default JobHistoryTable;

function getTimeDifference(startTime, endTime) {
  if (!startTime || !endTime) {
    return 0;
  }

  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);

  if (
    Number.isNaN(startHours) ||
    Number.isNaN(startMinutes) ||
    Number.isNaN(endHours) ||
    Number.isNaN(endMinutes)
  ) {
    return 0;
  }

  const startDate: any = new Date(0, 0, 0, startHours, startMinutes);
  const endDate: any = new Date(0, 0, 0, endHours, endMinutes);

  const diffMilliseconds = endDate - startDate;
  return Math.floor(diffMilliseconds / (1000 * 60));
}

function formatLateTime(minutes) {
  if (minutes < 60) return `${minutes} mins`;

  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;

  if (remainingMins === 0) {
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  }

  return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${remainingMins} mins`;
}
